import {
    Dispatch,
    ForwardedRef,
    forwardRef,
    Fragment,
    ReactNode,
    SetStateAction,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AssistantSmartTipsEntity } from '@mews-commander/data-access';
import { Spinner, Stack } from '@optimus-web/core';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { AdditionalSearchType, AssistantSearchType, SearchSubSection, SearchType } from '../assistantSearchTypes';
import { AssistantResult } from '../query/useAssistantSearchQuery';
import { SearchStateProps, ShowMoreOrLessStateProps } from '../Assistant';
import { SummarySidePanel } from '../summary/SummarySidePanel';
import Footer from './footer/Footer';
import { NoResult } from './noResult/NoResult';
import { AssistantContainerBackdrop, DrawerContainer, DrawerContainerStack, LoadingStack, ThinDivider } from './styles';
import { useSearchResultListComponent } from './hooks/useSearchResultListComponent';
import { AssistantSearchListItemProps } from './hooks/useAssistantSearchResultListMap';
import { AssistantSearchListItemButton } from './results/searchListItemButton';
import {
    DrawerChatResultContent,
    DrawerCommandResultContent,
    DrawerRecentAndFavoriteResultContent,
    DrawerSearchResultContent,
    DrawerSmartSearchResultContent,
} from './content/Content';
import { AssistantListItemSelectionStateProps } from './hooks/useAssistantListItemSelectionHandler';

interface DrawerProps {
    isMobileOrTablet: boolean;
    isFetching?: boolean;
    isFetched: boolean;
    hasResult: boolean;
    assistantResult?: AssistantResult;
    recentAndFavoriteListComponent?: ReactNode;
    chatPanelSearch?: boolean;
    assistantSearchResultListMap: Record<SearchType | SearchSubSection, AssistantSearchListItemProps[]>;
    searchResultList: AssistantSearchListItemProps[];
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    setShowMoreOrLessState: Dispatch<SetStateAction<ShowMoreOrLessStateProps>>;
    searchState: SearchStateProps;
    showMoreOrLessState: ShowMoreOrLessStateProps;
    selectedSearchType: AssistantSearchType;
    enterpriseIds: string[];
    recentAndFavoriteFetching?: boolean;
    handleUnFocused: () => void;
    listItemSelectionState: AssistantListItemSelectionStateProps;
    isTabNavigation: boolean;
    resultFilterChipComponent?: ReactNode;
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
}

export const Drawer = forwardRef(
    (
        {
            isMobileOrTablet,
            isFetched,
            isFetching,
            hasResult,
            assistantResult,
            chatPanelSearch = false,
            recentAndFavoriteListComponent,
            setSearchState,
            assistantSearchResultListMap,
            searchState,
            selectedSearchType,
            searchResultList,
            enterpriseIds,
            recentAndFavoriteFetching,
            setShowMoreOrLessState,
            handleUnFocused,
            showMoreOrLessState,
            listItemSelectionState,
            isTabNavigation,
            resultFilterChipComponent,
            setListItemSelectionState,
        }: DrawerProps,
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const drawerRef = useRef<HTMLDivElement>(null);
        const { searchTerm, focused } = searchState;
        const { selectedIndex, openSummary } = listItemSelectionState;
        const assistantSummaryEnabled = useFeatureFlag(FeatureFlag.assistantSummaryEnabled);
        const assistantSmartSearchEnabled = useFeatureFlag(FeatureFlag.assistantSmartSearchEnabled);
        const [hoveredResultType, setHoveredResultType] = useState<AssistantSearchType | undefined>();
        const showSearchResult = useMemo(
            () => searchTerm?.length > 0 && searchResultList.length > 0,
            [searchTerm, searchResultList.length]
        );
        const showChips = useMemo(() => !isMobileOrTablet && showSearchResult, [isMobileOrTablet, showSearchResult]);
        const showRecent = useMemo(
            () => searchTerm?.length < 1 && !!recentAndFavoriteListComponent,
            [recentAndFavoriteListComponent, searchTerm?.length]
        );
        const selectedItem = useMemo(() => searchResultList[selectedIndex - 1], [searchResultList, selectedIndex]);

        const searchResultListComponent = useSearchResultListComponent({
            assistantSearchResultListMap,
            selectedSearchType,
            showAll: selectedSearchType === AdditionalSearchType.All || isMobileOrTablet,
            setHoveredResultType,
            setShowMoreOrLessState,
            showMoreOrLessState,
            handleUnFocused,
            chatPanelSearch,
            isTabNavigation,
            searchResultList,
            selectedIndex,
        });

        const openSummaryPanel = useMemo(() => {
            if (!assistantSummaryEnabled) {
                return false;
            }

            if (openSummary) {
                const customerListItemProps = assistantSearchResultListMap[SearchType.Customer];
                const filteredItem =
                    openSummary &&
                    customerListItemProps.filter(
                        (item) =>
                            selectedItem &&
                            item.itemId === selectedItem?.itemId &&
                            selectedItem?.itemId !== AssistantSearchListItemButton.NewCustomer
                    );
                return filteredItem.length > 0;
            }
            return openSummary;
        }, [assistantSearchResultListMap, openSummary, selectedItem, assistantSummaryEnabled]);

        return (
            <Fragment>
                <DrawerContainer
                    ref={drawerRef}
                    isMobileOrTablet={isMobileOrTablet}
                    hasResult={hasResult}
                    focused={focused}
                    isFetched={isFetched}
                    isFetching={isFetching}
                    showRecent={showRecent}
                    chatPanelSearch={chatPanelSearch}
                    spacing="100"
                    vertical
                >
                    {!chatPanelSearch &&
                    (showRecent || isFetching || (hasResult && !isFetching) || (isFetched && !isFetching)) ? (
                        <ThinDivider />
                    ) : null}
                    {isFetching ? (
                        <LoadingStack itemAlignment="center" alignment="center">
                            <Spinner />
                        </LoadingStack>
                    ) : null}
                    {hasResult && !isFetching ? (
                        <DrawerContainerStack spacing="100" vertical>
                            {showSearchResult ? (
                                <>
                                    {showChips ? (
                                        <Stack spacing="100" itemAlignment="center">
                                            {/* {propertySelectorComponent} */}
                                            {resultFilterChipComponent}
                                        </Stack>
                                    ) : null}
                                    {assistantSmartSearchEnabled ? (
                                        <DrawerSmartSearchResultContent
                                            searchResultListComponent={searchResultListComponent}
                                        />
                                    ) : (
                                        <DrawerSearchResultContent
                                            searchResultListComponent={searchResultListComponent}
                                        />
                                    )}
                                </>
                            ) : null}
                            {assistantResult?.commandResult ? (
                                <DrawerCommandResultContent commandResult={assistantResult?.commandResult} />
                            ) : null}
                            {assistantResult?.chatResult ? (
                                <DrawerChatResultContent
                                    chatResult={assistantResult?.chatResult}
                                    searchTerm={searchTerm}
                                    showFollowUp={!chatPanelSearch}
                                    handleUnFocused={handleUnFocused}
                                />
                            ) : null}
                        </DrawerContainerStack>
                    ) : (
                        isFetched && !isFetching && <NoResult searchTerm={searchTerm} setSearchState={setSearchState} />
                    )}
                    {showRecent ? (
                        <>
                            {recentAndFavoriteFetching ? (
                                <LoadingStack itemAlignment="center" alignment="center">
                                    <Spinner />
                                </LoadingStack>
                            ) : (
                                <DrawerContainerStack spacing="100" vertical>
                                    <DrawerRecentAndFavoriteResultContent
                                        resentAndFavoriteListComponent={recentAndFavoriteListComponent}
                                    />
                                </DrawerContainerStack>
                            )}
                        </>
                    ) : null}
                    {!chatPanelSearch && (
                        <Footer
                            selectedItem={selectedItem}
                            hoveredResultType={hoveredResultType}
                            openSummary={openSummaryPanel}
                        />
                    )}
                </DrawerContainer>
                <SummarySidePanel
                    ref={ref}
                    searchType={SearchType.Customer}
                    enterpriseIds={enterpriseIds}
                    itemId={selectedItem?.itemId}
                    entity={AssistantSmartTipsEntity.Customer}
                    setSearchState={setSearchState}
                    setListItemSelectionState={setListItemSelectionState}
                    openSummaryPanel={openSummaryPanel}
                />
                {focused && <AssistantContainerBackdrop onClick={handleUnFocused} />}
            </Fragment>
        );
    }
);
